import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { 
  Mail, Phone, MapPin, Send, CheckCircle, 
  Clock, MessageCircle, Copy, Check
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  GSAPTextReveal, 
  ScrollScaleReveal,
  StaggeredCardsContainer,
  MaskReveal,
  MagneticButton,
  ParallaxElement,
  NeonGlowLine
} from '@/components/ui/GSAPAnimations';

const contactInfo = [
  {
    icon: Mail,
    title: 'Email Us',
    value: 'info@tattvatechworkz.com',
    action: 'Click to copy',
  },
  {
    icon: Phone,
    title: 'Call Us',
    value: '+91 89519 64093',
    action: 'Mon-Fri 9AM-6PM IST',
  },
  {
    icon: MapPin,
    title: 'Visit Us',
    value: 'Bangalore, Karnataka, India',
    action: 'By appointment only',
  },
];

const officeHours = [
  { day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM IST' },
  { day: 'Saturday', hours: '10:00 AM - 2:00 PM IST' },
  { day: 'Sunday', hours: 'Closed' },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  
  const planeRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailValid(value === '' ? null : emailRegex.test(value));
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('info@tattvatechworkz.com');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Paper plane animation
    if (planeRef.current) {
      gsap.to(planeRef.current, {
        x: window.innerWidth,
        y: -200,
        rotation: 45,
        scale: 0,
        duration: 1.5,
        ease: 'power2.in',
      });
    }

    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
      });
      setEmailValid(null);
    }, 4000);
  };

  return (
    <div className="pt-20 min-h-screen bg-void">
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient" />
        
        {/* Decorative Elements */}
        <ParallaxElement speed={-0.15} className="absolute top-20 right-20 opacity-20">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            className="w-48 h-48 border border-teal/30 rounded-full"
          />
        </ParallaxElement>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MaskReveal direction="up">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              [ Samvara Connection ]
            </span>
          </MaskReveal>
          
          <GSAPTextReveal 
            text="Let's Start a"
            className="text-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-2"
            splitBy="words"
            staggerDelay={0.1}
          />
          <GSAPTextReveal 
            text="Conversation"
            className="text-display text-4xl sm:text-5xl lg:text-6xl text-gradient-teal"
            splitBy="chars"
            staggerDelay={0.03}
            delay={0.5}
          />
          
          <MaskReveal direction="up">
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mt-6">
              Have questions about our products? Ready to transform your institution?
              We&apos;re here to help you on every step of the journey.
            </p>
          </MaskReveal>
        </div>
      </section>

      {/* ============================================
          MAIN CONTENT - Split Layout
          ============================================ */}
      <section className="py-16 relative">
        <div className="absolute top-0 left-0 right-0">
          <NeonGlowLine />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left Side - Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <MaskReveal direction="left">
                <h2 className="text-display text-2xl text-foreground mb-6">
                  Get in Touch
                </h2>
              </MaskReveal>
              
              {/* Contact Cards */}
              <StaggeredCardsContainer className="space-y-4">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    className="glass-card p-5 group cursor-pointer"
                    whileHover={{ x: 10, scale: 1.02 }}
                    onClick={info.title === 'Email Us' ? handleCopyEmail : undefined}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal/20 group-hover:shadow-teal-glow transition-all">
                        <info.icon className="w-5 h-5 text-teal" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground mb-1">
                          {info.title}
                        </p>
                        <p className="text-foreground font-display font-semibold">
                          {info.value}
                        </p>
                        {info.title === 'Email Us' ? (
                          <button
                            className="flex items-center gap-1 mt-2 text-xs text-teal hover:text-teal-light transition-colors"
                          >
                            {copied ? (
                              <>
                                <Check className="w-3 h-3" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                {info.action}
                              </>
                            )}
                          </button>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-1">
                            {info.action}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </StaggeredCardsContainer>

              {/* Office Hours */}
              <ScrollScaleReveal>
                <div className="glass-card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-orange" />
                    <h3 className="text-foreground font-display font-semibold">
                      Office Hours
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {officeHours.map((schedule) => (
                      <div key={schedule.day} className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-4 text-sm">
                        <span className="text-muted-foreground">{schedule.day}</span>
                        <span className="text-foreground font-mono text-xs sm:text-sm">{schedule.hours}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollScaleReveal>

              {/* Map Embed */}
              <ScrollScaleReveal>
                <div className="rounded-2xl overflow-hidden border border-glass-border h-64">
                  <iframe
                    title="Tattva Techworkz Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d248849.84916296514!2d77.49085452340765!3d12.954517009617162!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1670c9b44e6d%3A0xf8dfc3e8517e4fe0!2sBengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1703001234567!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) contrast(0.9)' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </ScrollScaleReveal>
            </div>

            {/* Right Side - Contact Form */}
            <div className="lg:col-span-3">
              <ScrollScaleReveal>
                <div className="glass-card p-4 sm:p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <MessageCircle className="w-5 h-5 text-teal" />
                    <h2 className="text-display text-2xl text-foreground">
                      Send us a Message
                    </h2>
                  </div>

                  <AnimatePresence mode="wait">
                    {submitSuccess ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex flex-col items-center justify-center py-20"
                      >
                        {/* Success Animation */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                          className="w-24 h-24 rounded-full bg-teal/20 flex items-center justify-center mb-6"
                        >
                          <CheckCircle className="w-12 h-12 text-teal" />
                        </motion.div>
                        <motion.h3
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="text-display text-2xl text-teal mb-2"
                        >
                          Message Sent!
                        </motion.h3>
                        <motion.p
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                          className="text-muted-foreground"
                        >
                          We&apos;ll get back to you within 24 hours.
                        </motion.p>
                      </motion.div>
                    ) : (
                      <motion.form
                        key="form"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleSubmit}
                        className="space-y-6"
                      >
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Name Field with Floating Label */}
                          <div className="relative">
                            <Input
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              onFocus={() => setFocusedField('name')}
                              onBlur={() => setFocusedField(null)}
                              required
                              className={cn(
                                'bg-void-light border-b-2 border-t-0 border-x-0 rounded-none px-0 pt-5',
                                'text-foreground transition-all duration-300',
                                'focus:ring-0',
                                focusedField === 'name' || formData.name ? 'border-teal' : 'border-muted-foreground/30'
                              )}
                            />
                            <label className={cn(
                              'absolute left-0 text-muted-foreground pointer-events-none',
                              'transition-all duration-300 origin-left',
                              focusedField === 'name' || formData.name
                                ? 'top-0 scale-75 text-teal'
                                : 'top-5'
                            )}>
                              Full Name *
                            </label>
                          </div>

                          {/* Email Field */}
                          <div className="relative">
                            <Input
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              onFocus={() => setFocusedField('email')}
                              onBlur={() => setFocusedField(null)}
                              required
                              className={cn(
                                'bg-void-light border-b-2 border-t-0 border-x-0 rounded-none px-0 pt-5',
                                'text-foreground transition-all duration-300',
                                'focus:ring-0',
                                emailValid === true && 'border-teal',
                                emailValid === false && 'border-destructive',
                                emailValid === null && (focusedField === 'email' ? 'border-teal' : 'border-muted-foreground/30')
                              )}
                            />
                            <label className={cn(
                              'absolute left-0 text-muted-foreground pointer-events-none',
                              'transition-all duration-300 origin-left',
                              (focusedField === 'email' || formData.email) && 'top-0 scale-75',
                              emailValid === true && 'text-teal',
                              emailValid === false && 'text-destructive',
                              !(focusedField === 'email' || formData.email) && 'top-5'
                            )}>
                              Email Address *
                            </label>
                            {emailValid === false && (
                              <motion.span 
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute right-0 top-5 text-xs text-destructive"
                              >
                                Invalid email
                              </motion.span>
                            )}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Phone Field */}
                          <div className="relative">
                            <Input
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleInputChange}
                              onFocus={() => setFocusedField('phone')}
                              onBlur={() => setFocusedField(null)}
                              className={cn(
                                'bg-void-light border-b-2 border-t-0 border-x-0 rounded-none px-0 pt-5',
                                'text-foreground transition-all duration-300',
                                'focus:ring-0',
                                focusedField === 'phone' || formData.phone ? 'border-teal' : 'border-muted-foreground/30'
                              )}
                            />
                            <label className={cn(
                              'absolute left-0 text-muted-foreground pointer-events-none',
                              'transition-all duration-300 origin-left',
                              focusedField === 'phone' || formData.phone
                                ? 'top-0 scale-75 text-teal'
                                : 'top-5'
                            )}>
                              Phone Number
                            </label>
                          </div>

                          {/* Company Field */}
                          <div className="relative">
                            <Input
                              name="company"
                              value={formData.company}
                              onChange={handleInputChange}
                              onFocus={() => setFocusedField('company')}
                              onBlur={() => setFocusedField(null)}
                              className={cn(
                                'bg-void-light border-b-2 border-t-0 border-x-0 rounded-none px-0 pt-5',
                                'text-foreground transition-all duration-300',
                                'focus:ring-0',
                                focusedField === 'company' || formData.company ? 'border-teal' : 'border-muted-foreground/30'
                              )}
                            />
                            <label className={cn(
                              'absolute left-0 text-muted-foreground pointer-events-none',
                              'transition-all duration-300 origin-left',
                              focusedField === 'company' || formData.company
                                ? 'top-0 scale-75 text-teal'
                                : 'top-5'
                            )}>
                              Institution / Company
                            </label>
                          </div>
                        </div>

                        {/* Subject Field */}
                        <div className="relative">
                          <Input
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('subject')}
                            onBlur={() => setFocusedField(null)}
                            required
                            className={cn(
                              'bg-void-light border-b-2 border-t-0 border-x-0 rounded-none px-0 pt-5',
                              'text-foreground transition-all duration-300',
                              'focus:ring-0',
                              focusedField === 'subject' || formData.subject ? 'border-teal' : 'border-muted-foreground/30'
                            )}
                          />
                          <label className={cn(
                            'absolute left-0 text-muted-foreground pointer-events-none',
                            'transition-all duration-300 origin-left',
                            focusedField === 'subject' || formData.subject
                              ? 'top-0 scale-75 text-teal'
                              : 'top-5'
                          )}>
                            Subject *
                          </label>
                        </div>

                        {/* Message Field */}
                        <div className="relative">
                          <Textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('message')}
                            onBlur={() => setFocusedField(null)}
                            required
                            rows={5}
                            className={cn(
                              'bg-void-light border-b-2 border-t-0 border-x-0 rounded-none px-0 pt-5',
                              'text-foreground resize-none transition-all duration-300',
                              'focus:ring-0',
                              focusedField === 'message' || formData.message ? 'border-teal' : 'border-muted-foreground/30'
                            )}
                          />
                          <label className={cn(
                            'absolute left-0 text-muted-foreground pointer-events-none',
                            'transition-all duration-300 origin-left',
                            focusedField === 'message' || formData.message
                              ? 'top-0 scale-75 text-teal'
                              : 'top-5'
                          )}>
                            Your Message *
                          </label>
                        </div>

                        {/* Submit Button with Paper Plane Animation */}
                        <div className="relative">
                          <MagneticButton strength={20} className="w-full">
                            <motion.button
                              ref={planeRef}
                              type="submit"
                              disabled={isSubmitting || emailValid === false}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className={cn(
                                'btn-primary w-full py-4 text-base relative overflow-hidden',
                                'disabled:opacity-50 disabled:cursor-not-allowed'
                              )}
                            >
                              {isSubmitting ? (
                                <motion.div
                                  initial={{ x: 0 }}
                                  animate={{ x: [0, 10, 0] }}
                                  transition={{ duration: 0.5, repeat: Infinity }}
                                  className="flex items-center gap-2"
                                >
                                  <Send className="w-5 h-5" />
                                  Sending...
                                </motion.div>
                              ) : (
                                <>
                                  <Send className="w-5 h-5" />
                                  Send Message
                                </>
                              )}
                            </motion.button>
                          </MagneticButton>
                        </div>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              </ScrollScaleReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
